import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

import {
	photoPlaceholder,
	photoUploadControls,
	photoUploadLabel,
	photoUploadSection,
} from '#src/app/pages/starterDashboardEndorsements/shared/utils';

import { PhotoActionButtons } from './components/PhotoActionButtons';
import { PhotoPreview } from './components/PhotoPreview';
import { UploadError } from './components/UploadError';
import type { PhotoUploadSectionProps } from './types';

export type { PhotoUploadSectionProps } from './types';

export const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({
	image,
	uppy,
	isInvalid = false,
	errorMessage,
	uploadErrorMessage,
	crop,
	uploader,
	onImageCrop,
	onCroppedPreviewChange,
}) => {
	const hasCropper = !!uploader && !!onImageCrop;

	return (
		<div className={photoUploadSection()}>
			<div className={photoUploadLabel()}>
				<Text variant="body" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_or_logo_label" />
				</Text>
				<Text variant="body" size="small" className="text-typography-weak">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_or_logo_text_support" />
				</Text>
			</div>

			<div className={photoUploadControls()}>
				<div
					className={cn(
						photoPlaceholder(),
						(isInvalid || !!uploadErrorMessage) && 'border-stroke-error-strong bg-fill-weaker border-2',
						image && hasCropper && 'relative overflow-hidden',
					)}
					data-testid="photo-placeholder"
				>
					<PhotoPreview
						image={image}
						hasCropper={hasCropper}
						crop={crop}
						uploader={uploader}
						onImageCrop={onImageCrop}
						onCroppedPreviewChange={onCroppedPreviewChange}
					/>
				</div>

				{image && hasCropper && (
					<Text variant="body" size="x-small" className="text-typography-weak">
						<Translate value="corgi.sap.media.cropper.reposition" />
					</Text>
				)}

				<PhotoActionButtons image={image} uppy={uppy} />
			</div>

			<UploadError uploadErrorMessage={uploadErrorMessage} errorMessage={errorMessage} />
		</div>
	);
};
